<script setup lang="ts">
// components
import SvgIcon from '~/components/SvgIcon.vue';
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import Buttons from '~/components/redesign/Buttons';
import ExpandableList from '~/components/ExpandableList.vue';
import AcquisitionModels from '~/components/redesign/AcquisitionModels.vue';
import AppVideo from '~/components/redesign/AppVideo';

// stores
import useUIStore from '~/stores/ui';

// composables
import { useLocalePathWithSlash, useApiSeo } from '~/composables';

// services
import { UserId } from '~/services';

// constants
import { PDF_PATHS, VIDEOS } from '~/constants';

// utils
import { getMuxPreview } from '~/utils';

const ANCHOR = 'talk-to-an-expert';
const QNA = [
  {
    heading: 'How are my data requirements determined?',
    content: `Our expert team will consult closely with you to determine your
      backup workload requirements, including parameters like daily change
      rates, anticipated data growth rates, retention policy and immutability
      settings. Our unique object storage sizing calculator allows us to model
      changes to any of these parameters to see the effects—instantly.`,
  },
  {
    heading: 'How are charges structured?',
    content: `Charges are simple and transparent. Backup data consumption is
      organized into preset Tiers and Steps. If you go above the threshold for
      your Step in each month, there's a modest per-Terabyte charge. If you're
      regularly going above the threshold, you can move up a Step or into a
      higher Tier—which will be more cost effective.`,
  },
  {
    heading: 'How long does the Consumption agreement run for?',
    content: `Ootbi Consumption agreements run for a year at a time. They do
      not auto-renew, though we will work with your Object First partner to
      ensure continuity of service into the next year towards the end of the
      agreement term. If you move up a step mid-term, a new agreement will be
      put in place, running for a further year.`,
  },
  {
    heading: 'What’s the minimum capacity in the Ootbi Consumption model?',
    content: 'The minimum commit is just 17 TB of backup storage.',
  },
  {
    heading: 'What’s the maximum capacity in the Ootbi Consumption model?',
    content: `With multiple Ootbi nodes, you can scale to many petabytes of
      backup data. Our experts will be happy to advise you.`,
  },
  {
    heading: 'How long do I keep the Ootbi appliance?',
    content: `You will keep the Ootbi for as long as your agreement runs,
      unless it becomes necessary to change it for operational reasons—for
      example, your data growth is such that a larger appliance is needed, or
      the appliance reaches the end of its operational life.`,
  },
  {
    heading: "What's the process if the Ootbi appliance gets changed?",
    content: `We will send a replacement Ootbi node to your premises. It's very
      straightforward to add the new node to your cluster and then decommission
      the previous node. If you need assistance this can be provided by your
      Object First partner or via a white-glove service from us. After
      decommissioning, we will provide a data destruction certificate for your
      records.`,
  },
  {
    heading: 'Is my agreement with Object First direct?',
    content: `Your agreement will be with your preferred Object First partner,
      although there will be some Object First Terms of Service that you will
      need to accept. We will work closely on an ongoing basis with you and
      your partner to ensure your needs are met.`,
  },
  {
    heading: 'Are there other hidden charges?',
    content: `No! Other than the normal step charges and supplemental charges
      for extra data consumption, there are no further charges from Object
      First during your agreement term.`,
  },
  {
    heading: 'Can I go down a step during the term of my agreement?',
    content: `We don’t anticipate this happening since data generally grows
      rather than contracts, and our sizing exercise will consider normal
      fluctuations in data consumption needs. If circumstances change
      significantly, a change can be made from the next agreement renewal.`,
  },
  {
    heading: 'Are there cases where Capex will be better than consumption?',
    content: `If the environment is static, not growing or changing (smaller or
      larger) over 5 years, CapEx may be appropriate.`,
  },
  {
    heading: 'I have an Ootbi now, can I change to a consumption model?',
    content: `No, an existing Ootbi appliance purchased under a CapEx model
      cannot be converted to a consumption model. However, you can run both
      models in your environment by deploying a separate Ootbi cluster
      specifically for consumption. This keeps storage usage tracking
      separate and accurate.`,
  },
  {
    heading: `Must a consumption agreement Ootbi system have telemetry
      enabled?`,
    content: `Yes, all consumption Ootbi systems must have telemetry enabled
      and operating. This means these systems have outbound https (port 443)
      internet access to Object First telemetry and consumption servers. If
      this is not configured and operating, Oobi systems will after a grace
      period stop accepting new backups.`,
  },
];

const uiStore = useUIStore();
const { setLoading, setLanguageSelectorVisibility } = uiStore;
const localePathWithSlash = useLocalePathWithSlash();
const { public: { env } } = useRuntimeConfig();

const isSubmitted = ref(false);
const setSubmitted = (value: boolean) => {
  isSubmitted.value = value;
};

const viewerId = ref<string>();
const playbackId = useState('playback-id', () =>
  env === 'production'
    ? VIDEOS.OOTBI_OLD_SIZES_PROD
    : VIDEOS.OOTBI_OLD_SIZES_DEV,
);
const videoPlayerRef = useTemplateRef('video');
const isVideoPreviewVisible = ref(true);
const setVideoPreviewVisibility = (value: boolean) => {
  isVideoPreviewVisible.value = value;
};

const playHandler = () => {
  if (!videoPlayerRef.value) return;

  setVideoPreviewVisibility(false);
  videoPlayerRef.value.play();
};

onMounted(() => {
  viewerId.value = UserId.get();
});

setLanguageSelectorVisibility(false);
onUnmounted(() => setLanguageSelectorVisibility(true));

useApiSeo('capex-consumption-acquisition-models-ootbi');
</script>

<template>
  <div data-page-name="capex-consumption-acquisition-models-ootbi">
    <section class="hero">
      <div class="wrapper">
        <SvgIcon name="best-storage-for-veeam" class="best-storage-for-veeam" />
        <b class="new">NEW</b>

        <h1 class="title">
          CapEx or<br />
          Consumption
        </h1>

        <b class="available-in-usa">Available in USA</b>

        <Buttons.Link :to="`#${ANCHOR}`">
          <span>Learn More</span>
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>

        <div class="background" />
      </div>
    </section>

    <div class="capex">
      <div class="wrapper">
        <AcquisitionModels
          :link="{
            to: PDF_PATHS.CAPEX_DATASHEET.en,
            target: '_blank',
          }"
          label="Read Data Sheet"
          lang="en"
        />
      </div>
    </div>

    <section class="why-consumption-based">
      <div class="wrapper">
        <h2>Why Consumption-Based Subscription?</h2>

        <ul class="cards">
          <li class="card">
            <SvgIcon name="darts-colorfull" />
            <strong>
              Pay for what you<br class="tablet" />
              use,<br class="desktop tablet" />
              grow as you<br class="tablet" />
              need
            </strong>

            <p>
              Backup capacity from 17 TB to 7 PB with no upfront payment, simple
              monthly billing and a 1-year subscription. Gain cost efficiency as
              you scale: the more you use, the less you pay per TB.**
            </p>

            <span>
              **Storage usage is assumed to grow over time; reducing consumption
              is possible at agreement renewal.
            </span>
          </li>

          <li class="card">
            <SvgIcon name="up-to-date-colorfull" />
            <strong>
              Hardware<br class="tablet" />
              refreshes<br class="desktop tablet" />
              included
            </strong>

            <p>
              All technology refreshes, service, support and software updates
              are included. You don’t pay for appliances, you pay for what you
              use.
            </p>
          </li>

          <li class="card">
            <SvgIcon name="personal-growth" />
            <strong>
              Make billing and management<br class="desktop tablet" />
              predictable
            </strong>
            <p>
              Get backup improvements faster, with no upfront outlay. Reduce
              risk of over- or under-sizing using Object First Storage
              Calculator, free expert support, and experienced partner network.
            </p>
          </li>
        </ul>

        <Buttons.Link :to="`#${ANCHOR}`">
          <span>Talk to an Expert</span>
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>
      </div>
    </section>

    <section class="how-it-works">
      <div class="wrapper">
        <h2>See how it works</h2>

        <div class="video-frame">
          <AppVideo.Preview
            :visible="isVideoPreviewVisible"
            :duration="126"
            :src="getMuxPreview(playbackId)"
            alt="Ootbi - Old Sizes"
            lang="en"
            @play="playHandler"
          />

          <AppVideo.Player
            ref="video"
            :visible="!isVideoPreviewVisible"
            :viewer-id
            :playback-id
            video-title="Ootbi - Old Sizes"
          />
        </div>
      </div>
    </section>

    <section class="three-ease-steps">
      <div class="wrapper">
        <h2>Simple to adopt in three easy steps</h2>

        <ul class="steps">
          <li class="step">
            <div class="left">
              <b>Step One</b>
            </div>

            <ul class="right">
              <li>
                <SvgIcon name="check-in-circle" />
                <p>Free consultation with Object First and Partner experts</p>
              </li>

              <li>
                <SvgIcon name="check-in-circle" />
                <p>
                  Plan how much backup data you'll likely need over the coming
                  years
                </p>
              </li>
            </ul>
          </li>

          <li class="step">
            <div class="left">
              <b>Step Two</b>
            </div>

            <ul class="right">
              <li>
                <SvgIcon name="check-in-circle" />
                <p>Place your order with your Object First Partner</p>
              </li>

              <li>
                <SvgIcon name="check-in-circle" />
                <p>We’ll ship you an Ootbi appliance to install on-premises</p>
              </li>
            </ul>
          </li>

          <li class="step">
            <div class="left">
              <b>Step Three</b>
            </div>

            <ul class="right">
              <li>
                <SvgIcon name="check-in-circle" />
                <p>
                  You’ll be billed a fixed monthly amount by your Object First
                  Partner
                </p>
              </li>

              <li>
                <SvgIcon name="check-in-circle" />
                <p>
                  If your usage goes above the baseline capacity in any month,
                  you’ll be billed for it in the following month, on a per-TB
                  basis
                </p>
              </li>

              <li>
                <SvgIcon name="check-in-circle" />
                <p>We unlock additional capacity as your needs grow</p>
              </li>
            </ul>
          </li>
        </ul>

        <Buttons.Link :to="`#${ANCHOR}`">
          <span>Free Consultation</span>
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>
      </div>
    </section>

    <section class="qna">
      <div class="wrapper">
        <h2>Q&A</h2>

        <ExpandableList :items="QNA" open-multiple-items />
      </div>
    </section>

    <section :id="ANCHOR">
      <div class="wrapper">
        <div class="left">
          <h2>Talk to an expert!</h2>
          <p>
            Interested in the Consumption model and need help with sizing your
            backup storage? Contact us!
          </p>
        </div>

        <div class="right">
          <Forms.Wrapper>
            <strong>Submit the form</strong>

            <Forms.RequestDemo
              product="Consumption Model"
              page="capex-consumption-acquisition-models-ootbi"
              gtm-event="contact"
              lang="en"
              @loading="setLoading"
              @submitted="setSubmitted(true)"
            />

            <transition name="fade">
              <Modals.ThankYou
                v-if="isSubmitted"
                :link="{
                  text: 'Home Page',
                  href: localePathWithSlash('/'),
                }"
                title="Thank you for your message."
                description="We're already working on your request."
              />
            </transition>
          </Forms.Wrapper>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/position';
@use '_/mixins/text-transform';
@use '_/fn';

[data-page-name='capex-consumption-acquisition-models-ootbi'] {
  overflow-x: hidden;

  section.hero {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    @include media.mobile {
      margin-bottom: 0;
    }

    .wrapper {
      position: relative;

      @include size.wrapper(80rem);
      padding: 8rem 2rem 5.87rem 2rem;

      @include flex.start-start;
      flex-direction: column;

      @include media.tablet {
        padding: 5rem 2rem 5.12rem 2rem;
      }

      @include media.mobile {
        padding: 2.5rem 1rem 5.12rem 1rem;
      }

      svg.best-storage-for-veeam {
        fill: fn.token('text-primary');
        margin-bottom: 4rem;

        width: 17.5rem;
        height: 5.5rem;

        @include media.mobile {
          width: 10rem;
          height: auto;
          margin-bottom: 1.25rem;
        }
      }

      b.new {
        @include typo.h4;
        color: fn.token('text-error');
        margin-bottom: 0.5rem;
      }

      h1.title {
        position: relative;
        @include typo.h1;
        margin-bottom: 0.5rem;

        @include media.mobile {
          margin-bottom: 0.4rem;
        }

        &:after {
          content: '*';
          font-size: 2.5rem;

          display: block;
          position: absolute;

          right: -0.85rem;
          bottom: 0.85rem;

          @include media.mobile {
            font-size: 1.5rem;
            right: -0.73rem;
            bottom: 0.4rem;
          }
        }
      }

      b.available-in-usa {
        @include typo.subtitle-1;
        margin-bottom: 4rem;

        @include media.mobile {
          margin-bottom: 1.2rem;
        }

        &:before {
          content: '*';
          font-size: 1.5rem;
          margin-right: -0.1rem;
        }
      }

      [data-component-name='Buttons.Link'] {
        @include media.mobile {
          width: 100%;
        }

        span {
          text-transform: capitalize;
          margin-left: 3.12rem;
        }

        svg {
          margin-right: 3.12rem;
        }
      }

      .background {
        position: absolute;
        right: -7.3rem;
        bottom: 0;
        z-index: -1;

        width: 1512px;
        height: 760px;

        background-image: url('/redesign/images/figures/purple-sphere-and-orange-triangle-desktop.webp');
        background-position: 0 0;
        background-size: 100% 100%;
        background-repeat: no-repeat;

        @include media.tablet {
          right: 0;
          bottom: 0;

          width: 768px;
          height: 660px;

          background-image: url('/redesign/images/figures/purple-sphere-and-orange-triangle-tablet.webp');
        }

        @include media.mobile {
          display: none;
        }
      }
    }
  }

  .capex {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5.25rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }
    }
  }

  section.why-consumption-based {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      @include size.wrapper(80rem);
      padding: 0 2rem;

      @include flex.start-sb;
      flex-direction: column;

      @include media.mobile {
        padding: 0 1rem;
      }

      h2 {
        @include typo.h3;
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 2rem;
        }
      }

      ul.cards {
        @include flex.start-sb;
        gap: 0 2rem;
        margin-bottom: 2rem;

        @include media.mobile {
          flex-direction: column;
        }

        li.card {
          @include size.fluid-max-width(24rem);

          @include flex.start-start;
          flex-direction: column;

          @include media.mobile {
            @include size.fluid-max-width(100%);
          }

          &:not(:last-of-type) {
            @include media.mobile {
              margin-bottom: 2rem;
            }
          }

          svg {
            @include size.fixed(3.125rem);
            margin-bottom: 2rem;
          }

          strong {
            @include typo.subtitle-1;
            @include text-transform.responsive-line-breaks;
            margin-bottom: 0.5rem;
          }

          p {
            @include typo.body-2;

            + span {
              margin-top: 1rem;
            }
          }

          span {
            @include typo.caption;

            @include media.tablet {
              display: none;
            }
          }
        }
      }

      [data-component-name='Buttons.Link'] {
        margin: 0 auto;

        @include media.mobile {
          width: 100%;
        }

        span {
          margin-left: 1.66rem;
        }

        svg {
          margin-right: 1.66rem;
        }
      }
    }
  }

  section.how-it-works {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }

      h2 {
        @include typo.h3;
        text-align: center;
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 2rem;
        }
      }

      .video-frame {
        position: relative;
        aspect-ratio: 16 / 9;
        z-index: 0;

        @include size.wrapper(50rem);
      }
    }
  }

  section.three-ease-steps {
    color: fn.token('text-reversed');
    background-color: fn.token('surf-cont-secon-action');

    .wrapper {
      @include size.wrapper(80rem);
      padding: 4.25rem 2rem;

      @include flex.stretch-sb;
      flex-direction: column;

      @include media.tablet {
        padding: 3.75rem 2rem;
      }

      @include media.mobile {
        padding: 3.25rem 1rem;
      }

      h2 {
        @include typo.h3;
      }

      ul.steps {
        margin: 2rem 0;

        @include media.tablet {
          margin: 0;
        }

        li.step {
          @include flex.start-start;
          padding: 2rem 0;

          @include media.mobile {
            flex-direction: column;
          }

          &:nth-child(2) {
            border: 1px solid fn.token('outline-reversed');
            border-left: unset;
            border-right: unset;
          }

          .left {
            @include size.fluid-max-width(26rem);

            @include media.tablet {
              @include size.fluid-max-width(15rem);
            }

            @include media.mobile {
              width: max-content;
              margin-bottom: 1rem;
            }

            b {
              @include typo.h4;
            }
          }

          ul.right {
            li {
              @include flex.start-start;
              gap: 0 1rem;

              &:not(:last-of-type) {
                margin-bottom: 1rem;
              }

              svg {
                @include size.fixed(1.5rem);
                fill: fn.token('icon-reversed');
              }

              p {
                @include typo.body-2;
              }
            }
          }
        }
      }

      [data-component-name='Buttons.Link'] {
        width: max-content;
        margin-left: auto;

        @include media.mobile {
          width: 100%;
        }
      }
    }
  }

  section.qna {
    .wrapper {
      @include size.wrapper(80rem);
      padding: 10.25rem 2rem;

      @include media.tablet {
        padding: 5rem 2rem;
      }

      @include media.mobile {
        padding: 5rem 1rem;
      }

      h2 {
        @include typo.h3;
        margin-bottom: 2rem;
      }
    }
  }

  section#talk-to-an-expert {
    position: relative;
    overflow: hidden;

    background: url('/redesign/images/purple-sphere-and-cone.webp');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 50% 50%;

    .wrapper {
      position: relative;
      z-index: 1;

      padding: 8rem 2rem;
      @include size.wrapper(80rem);

      @include flex.stretch-sb;
      gap: 0 2rem;

      @include media.tablet {
        flex-direction: column;
        padding: 5rem 2rem;
        gap: 2rem 0;
      }

      @include media.mobile {
        padding: 2.5rem 1rem;
        gap: 1.5rem 0;
      }

      .left,
      .right {
        width: 100%;
      }

      .left {
        margin-top: 5rem;
        color: fn.token('text-reversed');

        @include media.tablet {
          margin-top: 0;
        }

        h2 {
          @include typo.h3;
          margin-bottom: 2rem;

          @include media.tablet {
            margin-bottom: 1rem;
          }
        }

        p {
          @include typo.body-1;
        }
      }

      .right {
        @include media.tablet {
          @include size.wrapper(37rem);
        }

        [data-component-name='Forms.Wrapper'] {
          position: relative;

          strong {
            display: block;
            @include typo.h4;
            margin-bottom: 1.5rem;
          }

          [data-component-name='Modals.ThankYou'] {
            @include position.absolute-full;
            z-index: 1;
            padding: 2rem;
            background-color: fn.token('surf-cont-primary');

            @include media.mobile {
              padding: 1.5rem 1rem;
            }
          }
        }
      }
    }
  }
}
</style>
